import httpClient from "../axios/interceptors";
import { handleResponseError } from "helpers/utils";
import { API } from "./endpoint";
import { AxiosResponse } from "axios";
import { CheckPriceInput, CheckPriceResponse } from "./apiSpeedShipping";

export type FactoryParcelAddress = {
  fullname: string;
  address: string;
  county: string;
  city: string;
  state: string;
  postcode: string;
  email?: string;
  telephone1: string;
  telephone2?: string;
  address_id?: string;
  province_id?: number;
  district_id?: number;
  sub_district_id?: number;
};

export type FactoryParcelAddressLookupItem = FactoryParcelAddress & {
  address_id: string;
  source: "factory_parcel" | "agent" | "retail";
  ref_type: string;
  ref_id: string;
  label: string;
  /** จาก address.create_date — เรียงลำดับรอง (ใหม่ก่อน) */
  create_date?: string;
  /** จาก address.update_date — fallback เรียง */
  updated_at?: string;
  /** ที่อยู่ปลายทางล่าสุดจากประวัติส่งพัสดุ standalone */
  last_address?: boolean;
};

export type FactoryParcelProduct = {
  qty: number;
  code: string;
  name: string;
  price: number;
  /** น้ำหนักต่อหน่วย (กรัม) */
  weight?: number;
};

export type FactoryParcelPackagingSnapshot = {
  type_label?: string;
  box_width?: number;
  box_length?: number;
  box_height?: number;
  box_weight?: number;
};

export type FactoryCreateParcelInput = {
  origin_mode: "merchant" | "custom";
  origin?: FactoryParcelAddress;
  destination_address_id?: string;
  carrier_code: string;
  box_width?: number;
  box_height?: number;
  box_length?: number;
  box_weight: number;
  cod_amount?: number;
  reference_no?: string;
  external_id?: string;
  remark?: string;
  destination: FactoryParcelAddress;
  products: FactoryParcelProduct[];
  /** ราคาประมาณจาก check-price ตอนเลือก carrier */
  estimate_price?: number;
  packaging_type_label?: string;
};

export type ParcelSpeedQueueStatus =
  | "PENDING"
  | "PROCESSING"
  | "SUCCESS"
  | "FAILED"
  | "UNKNOWN";

export type FactoryParcelItem = {
  id: string;
  tracking_number?: string | null;
  carrier_code: string;
  status: string;
  speed_queue_status?: ParcelSpeedQueueStatus | string;
  error_message?: string | null;
  reference_no?: string;
  external_id?: string;
  destination_snapshot?: FactoryParcelAddress & { fullname?: string };
  products_snapshot?: FactoryParcelProduct[];
  packaging_snapshot?: FactoryParcelPackagingSnapshot;
  remark?: string;
  estimate_price?: number;
  charge?: number;
  wallet_balance?: number;
  price_surcharge?: number;
  cod_fee_surcharge?: number;
  remote_surcharge?: number;
  tourism_surcharge?: number;
  island_surcharge?: number;
  warranty_price?: number;
  credit_used?: number;
  outstanding_debt?: number;
  credit_before?: number;
  credit_after?: number;
  withdraw?: number;
  cod_amount?: number;
  label_url?: string;
  is_print?: boolean;
  create_date?: string;
  update_date?: string;
  /** เวลาที่ได้ tracking_number/label จาก Speed จริง — ใช้คำนวณ deadline ยกเลิกแทน create_date */
  label_obtained_at?: string;
};

export type FactoryParcelListResponse = {
  status?: string;
  data?: {
    items: FactoryParcelItem[];
    total: number;
    page: number;
    limit: number;
    viewpoint: string;
  };
};

export type FactoryCreateParcelResponse = {
  status?: string;
  data?: {
    parcel_id: string;
    speed_queue_status: ParcelSpeedQueueStatus;
    reference_no: string;
    parcel?: FactoryParcelItem;
    tracking_number?: string;
    label_url?: string;
  };
};

export const findFactoryParcelAddressesByPhone = async (
  telephone: string
): Promise<{ status?: string; data?: { items: FactoryParcelAddressLookupItem[] } }> => {
  const data = await httpClient
    .get(API.factoryParcelAddressByPhone, { params: { telephone } })
    .catch((error) => handleResponseError(error));
  return data.data;
};

export const createFactoryParcel = async (
  input: FactoryCreateParcelInput
): Promise<FactoryCreateParcelResponse> => {
  const data: AxiosResponse<FactoryCreateParcelResponse> = await httpClient
    .post(API.factoryParcelCreate, input, { timeout: 30000 })
    .catch((error) => handleResponseError(error));
  return data.data;
};

export const retryFactoryParcel = async (
  parcelId: string
): Promise<{
  status?: string;
  data?: {
    parcel_id: string;
    speed_queue_status: string;
    tracking_number?: string;
    reference_no?: string;
  };
  /** ข้อความ error จาก backend เมื่อ status !== "success" (เช่น INSUFFICIENT_CREDIT) — HttpExceptionFilter
   * ส่ง HTTP 200 เสมอแม้ error จึงต้องอ่านจาก body นี้แทน axios reject (ดู http-exception.filter.ts) */
  error_message?: string | string[];
}> => {
  const data = await httpClient
    .post(API.factoryParcelRetry(parcelId))
    .catch((error) => handleResponseError(error));
  return data.data;
};

export const listFactoryParcels = async (params: {
  viewpoint?: string;
  search?: string;
  start?: string;
  end?: string;
  /** brand_code หลายค่า — ส่งเป็น string คั่นด้วย comma */
  carrier_codes?: string;
  page?: number;
  limit?: number;
}): Promise<FactoryParcelListResponse> => {
  const data: AxiosResponse<FactoryParcelListResponse> = await httpClient
    .get(API.factoryParcelList, { params })
    .catch((error) => handleResponseError(error));
  return data.data;
};

export const getFactoryParcelDetail = async (
  id: string
): Promise<{ status?: string; data?: { parcel: FactoryParcelItem; logs: unknown[] } }> => {
  const data = await httpClient
    .get(API.factoryParcelDetail(id))
    .catch((error) => handleResponseError(error));
  return data.data;
};

export const printFactoryParcel = async (input: {
  tracking_number: string;
  carrier_code: string;
  show_order?: string;
}): Promise<unknown> => {
  const data = await httpClient
    .post(API.factoryParcelPrint, input, { timeout: 60000 })
    .catch((error) => handleResponseError(error));
  return data.data;
};

export const markFactoryParcelPrint = async (input: {
  parcel_ids: string[];
}): Promise<{ status?: string; data?: { updated_count: number } }> => {
  const data = await httpClient
    .post(API.factoryParcelMarkPrint, input)
    .catch((error) => handleResponseError(error));
  return data.data;
};

export const cancelFactoryParcel = async (
  trackingNumber: string
): Promise<unknown> => {
  const data = await httpClient
    .put(API.factoryParcelCancel(trackingNumber))
    .catch((error) => handleResponseError(error));
  return data.data;
};

export { checkSpeedShippingPrice } from "./apiSpeedShipping";
export type { CheckPriceInput, CheckPriceResponse };
